import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { NavigationArrow, House, Briefcase, MapPin } from "@phosphor-icons/react";

/**
 * Reusable Add/Edit address form (modal or inline).
 * Props:
 *   initial?: address to edit (with id)
 *   onClose: () => void
 *   onSaved: (addr) => void
 *   defaultPincode?: prefill
 */
export default function AddressForm({ initial, onClose, onSaved, defaultPincode = "" }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    label: initial?.label || "Home",
    receiver_name: initial?.receiver_name || "",
    receiver_mobile: initial?.receiver_mobile || "",
    address: initial?.address || "",
    landmark: initial?.landmark || "",
    city: initial?.city || "",
    state: initial?.state || "",
    pincode: initial?.pincode || defaultPincode || "",
    lat: initial?.lat ?? null,
    lon: initial?.lon ?? null,
    is_default: initial?.is_default || false,
  });
  const [busy, setBusy] = useState(false);
  const [detecting, setDetecting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const detect = () => {
    if (!navigator.geolocation) return toast.error("Location not supported");
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const r = await api.get("/customer/reverse-geocode", {
            params: { lat: latitude, lon: longitude },
          });
          if (r.data.pincode) {
            update("pincode", r.data.pincode);
            update("city", r.data.city || form.city);
            update("state", r.data.state || form.state);
            update("lat", latitude);
            update("lon", longitude);
            const where = r.data.locality || r.data.city || "your area";
            toast.success(`Detected: ${where} · ${r.data.pincode}`);
          } else {
            toast.error("Couldn't detect pincode. Please enter manually.");
          }
        } catch {
          toast.error("Couldn't detect location.");
        }
        setDetecting(false);
      },
      () => {
        toast.error("Location permission denied.");
        setDetecting(false);
      },
      { timeout: 10000, enableHighAccuracy: false, maximumAge: 60000 }
    );
  };

  const save = async (e) => {
    e?.preventDefault();
    if (!form.receiver_name.trim()) return toast.error("Enter receiver name");
    if (!/^\d{10}$/.test(form.receiver_mobile)) return toast.error("Enter 10-digit mobile");
    if (!form.address.trim()) return toast.error("Enter address");
    if (!/^\d{6}$/.test(form.pincode)) return toast.error("Enter valid 6-digit pincode");
    setBusy(true);
    try {
      let saved;
      if (isEdit) {
        const r = await api.patch(`/customer/addresses/${initial.id}`, form);
        saved = r.data;
      } else {
        const r = await api.post("/customer/addresses", form);
        saved = r.data;
      }
      toast.success(isEdit ? "Address updated" : "Address saved");
      onSaved(saved);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save address");
    }
    setBusy(false);
  };

  const labelOptions = [
    { value: "Home", icon: House },
    { value: "Work", icon: Briefcase },
    { value: "Other", icon: MapPin },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 fade-up"
      onClick={onClose}>
      <div className="bg-white w-full max-w-md max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-2xl shadow-2xl p-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="address-form-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-black text-xl">{isEdit ? "Edit address" : "Add new address"}</h2>
          <button onClick={onClose} className="text-2xl leading-none text-gray-400" aria-label="Close">×</button>
        </div>

        <button
          type="button"
          onClick={detect}
          disabled={detecting}
          data-testid="address-detect-btn"
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-gray-200 hover:border-[#15803D] hover:bg-[#15803D]/5 transition font-semibold text-sm mb-4"
        >
          <NavigationArrow weight={detecting ? "fill" : "bold"} size={16} className="text-[#15803D]" />
          {detecting ? "Detecting…" : "Use current location"}
        </button>

        <form onSubmit={save} className="space-y-3">
          {/* Label */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-1.5">Save as</div>
            <div className="flex gap-2">
              {labelOptions.map((o) => {
                const Icon = o.icon;
                const active = form.label === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => update("label", o.value)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold border-2 transition ${active ? "border-[#15803D] bg-[#15803D]/5 text-[#15803D]" : "border-gray-100 text-gray-700"}`}
                    data-testid={`address-label-${o.value.toLowerCase()}`}
                  >
                    <Icon size={14} weight={active ? "fill" : "regular"} />
                    {o.value}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <input className="qd-input" placeholder="Receiver name" value={form.receiver_name}
              onChange={(e) => update("receiver_name", e.target.value)} data-testid="address-name" />
            <input className="qd-input" placeholder="Mobile" maxLength={10} value={form.receiver_mobile}
              onChange={(e) => update("receiver_mobile", e.target.value.replace(/\D/g, ""))} data-testid="address-mobile" />
          </div>

          <textarea
            className="qd-input min-h-[64px] resize-none"
            placeholder="House / Flat / Floor / Street"
            value={form.address}
            onChange={(e) => update("address", e.target.value)}
            data-testid="address-line"
          />

          <input className="qd-input" placeholder="Landmark (optional)" value={form.landmark}
            onChange={(e) => update("landmark", e.target.value)} data-testid="address-landmark" />

          <div className="grid grid-cols-3 gap-2">
            <input className="qd-input col-span-1 font-mono" maxLength={6} placeholder="Pincode" value={form.pincode}
              onChange={(e) => update("pincode", e.target.value.replace(/\D/g, ""))} data-testid="address-pincode" />
            <input className="qd-input col-span-1" placeholder="City" value={form.city}
              onChange={(e) => update("city", e.target.value)} data-testid="address-city" />
            <input className="qd-input col-span-1" placeholder="State" value={form.state}
              onChange={(e) => update("state", e.target.value)} data-testid="address-state" />
          </div>

          <label className="flex items-center gap-2 text-sm pt-1">
            <input type="checkbox" checked={form.is_default}
              onChange={(e) => update("is_default", e.target.checked)}
              data-testid="address-default-toggle" />
            Set as default address
          </label>

          <button type="submit" disabled={busy} className="qd-btn-green w-full mt-2" data-testid="address-save-btn">
            {busy ? "Saving…" : (isEdit ? "Update address" : "Save address")}
          </button>
        </form>
      </div>
    </div>
  );
}
